import { IconDefinitions } from "../../../components";
import { GaugeConfig, UploadSessionModel } from "../../../data/entities";

export const formatBytes = (bytes: number): string => {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_024).toFixed(1)} KB`;
};

export const formatUptime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const getFileName = (filePath: string) => {
  return filePath?.split("\\").pop()?.split("/").pop() || filePath || "—";
};

export const getProgress = (session: UploadSessionModel) => {
  if (!session.totalChunks || session.totalChunks === 0) return 0;
  return Math.floor((session.uploadedChunks / session.totalChunks) * 100);
};

export const getDurationLabel = (startDate: string, endDate: string) => {
  const durationMs =
    new Date(endDate).getTime() - new Date(startDate).getTime();
  const durationSec = Math.round(durationMs / 1000);
  if (durationSec >= 3600) {
    const h = Math.floor(durationSec / 3600);
    const m = Math.floor((durationSec % 3600) / 60);
    return `${h}h ${m}m`;
  } else if (durationSec >= 60) {
    const m = Math.floor(durationSec / 60);
    const s = durationSec % 60;
    return `${m}m ${s}s`;
  }
  return `${durationSec}s`;
};

export function getOverallStatus(
  failedCount: number,
  warningCount: number,
  total: number,
  gaugeLevel: GaugeLevel = "none",
) {
  if (failedCount > 0 || gaugeLevel === "critical")
    return {
      label: "Issues Found",
      variant: "danger",
      icon: IconDefinitions.circleExclamation,
    };
  if (warningCount > 0 || gaugeLevel === "caution")
    return {
      label: "Passed with Warnings",
      variant: "warning",
      icon: IconDefinitions.triangleExclamation,
    };
  if (total === 0)
    return {
      label: "No Data",
      variant: "secondary",
      icon: IconDefinitions.clock,
    };
  return {
    label: "All Checks Passed",
    variant: "success",
    icon: IconDefinitions.circleCheck,
  };
}

export function formatPeakTimestamp(isoString: string): { utc: string; local: string } {
  const date = new Date(isoString);
  const utc = date.toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }) + ' UTC';
  const local = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
  return { utc, local };
}

export function calcPct(part: number, total: number) {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

export function displayPct(count: number, total: number): string {
  if (total <= 0 || count <= 0) return "0";
  const raw = (count / total) * 100;
  if (raw < 1) return "< 1";
  if (raw > 99 && count < total) return "> 99";
  return String(Math.round(raw));
}


export function daysUntilReset(resetDate?: Date): number | null {
  if (!resetDate) return null;
  const today = new Date();
  const diff  = resetDate.getTime() - today.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}


export function rawToPercent(rawValue: number, config: GaugeConfig): number {
  const { cautionThreshold, criticalThreshold, capPercent, cautionFlawValue, criticalFlawValue } = config;
 
  if (rawValue <= 0) return 0;
 
  if (rawValue <= cautionFlawValue) {
    return (rawValue / cautionFlawValue) * cautionThreshold;
  }
 
  const result = cautionThreshold +                                                   // Start at caution Threshold (ex. 20%)         
    ((rawValue - cautionFlawValue) / (criticalFlawValue - cautionFlawValue)) *        // (how far into the the flaw range) / (flaw range) 
    (criticalThreshold - cautionThreshold);                                           // percent range
  return Math.min(result, capPercent);
}
 


export function deriveFlwGaugeConfig(config: GaugeConfig | null): GaugeConfig | null {
  if (!config?.flwCautionDifference || !config.flwCriticalDifference) return null;
  return {
    ...config,
    cautionFlawValue: config.flwCautionDifference,
    criticalFlawValue: config.flwCriticalDifference,
  };
}

export type GaugeLevel = "none" | "caution" | "critical";

export function getGaugeLevel(rawValue: number | null | undefined, config: GaugeConfig | null): GaugeLevel {
  if (rawValue == null || !config) return "none";
  const pct = rawToPercent(rawValue, config);
  if (pct >= config.criticalThreshold) return "critical";
  if (pct >= config.cautionThreshold) return "caution";
  return "none";
}

export function worstGaugeLevel(...levels: GaugeLevel[]): GaugeLevel {
  if (levels.includes("critical")) return "critical";
  if (levels.includes("caution")) return "caution";
  return "none";
}

export function percentToGaugeValue(pct: number, config: GaugeConfig): number {
  const { cautionThreshold, criticalThreshold, capPercent } = config;
 
  if (pct <= cautionThreshold) {
    return (pct / cautionThreshold) * 33.33;
  }
  if (pct <= criticalThreshold) {
    return 33.33 + ((pct - cautionThreshold) / (criticalThreshold - cautionThreshold)) * 33.33;
  }
  return Math.min(
    66.67 + ((pct - criticalThreshold) / (capPercent - criticalThreshold)) * 33.33,
    100
  );
}
